import { defineStore } from 'pinia'
import { request, fetchCategoryElementsData } from '../../api'

export const useAiDesignerStore = defineStore('aiDesigner', {
  state: () => ({
    loading: false,
    loadMore: false,
    data: null,
    steps: null,
    attributeDependency: null,
    checked: {},
    designIds: {},
    designs: [],
    elements: [],
    designPage: 1,
  }),
  getters: {
    stepsCount () {
      return this.steps?.length + 1
    },
    filterSelected () {
      const checked = Object.values(this.checked).flat()
      return checked
    },
    filterDeps () {
      let res = []
      this.filterSelected.forEach(id => {
        const attrs = this.attributeDependency[id] || []
        res = [...res, ...attrs]
      })
      return res
    },
  },
  actions: {
    allReset () {
      this.data = null
      this.steps = null
      this.attributeDependency = null
      this.checked = {}
      this.designIds = {}
      this.designs = []
      this.elements = []
      this.designPage = 1
    },
    resetForAiInspiration () {
      this.data = null
      this.designIds = {}
      this.designs = []
      this.elements = []
      this.designPage = 1
    },
    async getList (category) {
      this.loading = true
      try {
        const { data: { designer = {}, attribute_dependency: attributeDependency = {} } } = await request.get(
          `/ai-designer/${category}`,
        )
        this.data = designer
        this.attributeDependency = attributeDependency
        this.steps = designer?.steps
        designer.steps.forEach((el, index) => {
          this.checked[index] = []
        })
        this.loading = false
      } catch (e) {
        this.loading = false
      }
    },
    async getDesigns (category) {
      if (this.designPage === 1) this.loading = true
      else this.loadMore = true
      const allArray = Object.values(this.checked).flat()
      try {
        const { data } = await request.post(
          `/ai-designer/${category}`,
          JSON.stringify({
            attributeIds: allArray.toString(),
            page: this.designPage,
          }),
        )
        this.designIds[this.designPage] = data
        this.designPage++
        this.fetchDesign(category)
        this.loading = false
        this.loadMore = false
      } catch (e) {
        this.loading = false
        this.loadMore = false
      }
    },
    async fetchDesign (category) {
      for (let i in this.designIds[this.designPage - 1]) {
        const elementIds = this.designIds[this.designPage - 1][i]
        this.designs.push({
          loading: true,
          error: false,
          data: null,
          elements: null,
          designOptions: null,
        })
        fetchCategoryElementsData(category, elementIds)
          .then((data) => {
            let index = i - 1
            let elements = []
            let settingCodes = []
            function getUniqueKeys (settingCodes) {
              return settingCodes.reduce((acc, code) => {
                const key = code.split(':')[0]
                if (!acc.includes(key)) {
                  acc.push(key)
                }
                return acc
              }, [])
            }
            for (let i in data) {
              if (data[i].element.settingCodes) {
                let codes = getUniqueKeys(data[i].element.settingCodes)
                codes.forEach(code => {
                  let defaultSetting = data[i].element.attributes.find(attr => attr.code.includes(code))
                  if (defaultSetting) settingCodes.push(defaultSetting.code)
                })
              }
              const element = {
                attributes: data[i].element.attributes,
                nodeCode: data[i].node.code,
                params: data[i].element.params,
                svgBack: data[i].element.svgBack,
                svgFront: data[i].element.svgFront,
              }
              elements.push(element)
            }
            let designIndex = (this.designPage - 2) * 10 + index
            this.designs.splice(designIndex, 1, {
              loading: false,
              data: data,
              elements: elements,
              designOptions: {
                designCategory: category,
                designName: "Untitled",
                eases: [],
                elementIds: elementIds,
                fabricType: undefined,
                fit: undefined,
                internalLines: 2,
                mannequinId: "8e7f46f46a5e440bb33d2bd031737f10",
                seamAllowance: 0,
                settingCodes: settingCodes,
              },
              error: false,
            })
          })
          .catch((e) => {
            console.log('e', e)
            let index = i - 1
            let designIndex = (this.designPage - 2) * 10 + index
            this.designs.splice(designIndex, 1, {
              loading: false,
              data: null,
              elements: null,
              designOptions: null,
              error: true,
            })
          })
      }
    },
    checkedToggle (id, i) {
      const checked = [...this.checked[i]]
      let index = checked.indexOf(id)
      if (index !== -1) {
        checked.splice(index, 1)
      } else checked.push(id)
      this.checked = {
        ...this.checked,
        [i]: checked,
      }
    },
    filterByDeps (list = [], step = 1) {
      // First step - no filtering needed
      if (step === 1) return list

      if (!this.attributeDependency) return list

      // For each previous step, get the UNION of compatible attributes (within step)
      const compatibleByStep = []
      for (let i = 0; i < step - 1; i++) {
        const checked = this.checked[i] || []
        if (checked.length === 0) continue

        // Union of compatible IDs for selections within this step
        const stepCompatible = new Set()
        checked.forEach(selectedId => {
          const compatibleIds = this.attributeDependency[selectedId] || []
          compatibleIds.forEach(id => stepCompatible.add(id))
        })

        if (stepCompatible.size > 0) {
          compatibleByStep.push(stepCompatible)
        }
      }

      // If no previous selections with dependencies, show all
      if (compatibleByStep.length === 0) return list

      // Filter: attribute must be in ALL step-level compatible sets (INTERSECTION across steps)
      return list.filter(item => {
        const itemId = String(item.id)
        return compatibleByStep.every(stepSet => stepSet.has(itemId))
      })
    },
  },
})
